import { messages } from "./messages";
import { MIGRATION_PHASES, FILTER_KEYS } from "./types";

import type { FastshiftSupportRequest } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";

/**
 * Returns the display message for a migration phase.
 * For unknown phases, returns a fallback message instead of null to prevent UI crashes.
 *
 * @param phase - The migration phase string
 * @returns The internationalized message for the phase, or a fallback for unknown phases
 */
export const getMigrationPhaseDisplayText = (phase: string) => {
    switch (phase) {
        case MIGRATION_PHASES.EXPLORE:
            return messages.migrationTicketsTableMigrationPhaseExplore;
        case MIGRATION_PHASES.ASSESS:
            return messages.migrationTicketsTableMigrationPhaseAssess;
        case MIGRATION_PHASES.PLAN_AND_TRACK:
            return messages.migrationTicketsTableMigrationPhasePlanAndTrack;
        case MIGRATION_PHASES.TEST_MIGRATION:
            return messages.migrationTicketsTableMigrationPhaseTestMigration;
        case MIGRATION_PHASES.PROD_MIGRATION:
            return messages.migrationTicketsTableMigrationPhaseProdMigration;
        case MIGRATION_PHASES.POST_MIGRATION:
            return messages.migrationTicketsTableMigrationPhasePostMigration;
        default:
            return messages.migrationTicketsTableMigrationPhaseUnknown;
    }
};

/**
 * Apply filters to support requests data
 */
export const applyFilters = (
    data: FastshiftSupportRequest[],
    filters: Record<string, string | null>
): FastshiftSupportRequest[] => {
    if (!filters || Object.keys(filters).length === 0) return data;
    const migrationPhaseFilter = filters[FILTER_KEYS.MIGRATION_PHASE];
    const statusFilter = filters[FILTER_KEYS.STATUS];

    return data.filter((item) => {
        const hasMigrationPhaseFilter = migrationPhaseFilter?.trim();
        const hasStatusFilter = statusFilter?.trim();

        if (
            hasMigrationPhaseFilter &&
            item.migrationPhase !== migrationPhaseFilter
        ) {
            return false;
        }

        if (hasStatusFilter && item.fastShiftStatusReason !== statusFilter) {
            return false;
        }

        return true;
    });
};

/**
 * Apply search filter to support requests data
 */
export const applySearch = (
    data: FastshiftSupportRequest[],
    searchTerm: string | undefined
): FastshiftSupportRequest[] => {
    if (!searchTerm?.trim()) return data;

    const term = searchTerm.toLowerCase();
    return data.filter(
        (req) =>
            req.supportTicketId.toLowerCase().includes(term) ||
            req.title.toLowerCase().includes(term)
    );
};
